import { hasWidgetHydrationPayload } from '../../builderCore'
import { areDateRangesEqual, createEmptyDashboardDateRange } from '../../runtime'
import { isDimensionSortKey, resolveDefaultDateSortKey } from '../../sortUtils'
import type { DashboardViewAction, DashboardViewState, WidgetSessionState } from './state'
import {
  createDashboardGlobalFilterState,
  createWidgetSessionState,
  createWidgetStateMap,
  hasWidgetData,
} from './state'

function getWidgetSession(state: DashboardViewState, widgetId: string): WidgetSessionState {
  if (state.widgets[widgetId]) {
    return state.widgets[widgetId]
  }

  const widget = state.dashboard?.widgets.find((item) => item.id === widgetId)
  return createWidgetSessionState(widget)
}

export function dashboardViewReducer(state: DashboardViewState, action: DashboardViewAction): DashboardViewState {
  switch (action.type) {
    case 'BOOTSTRAP_STARTED':
      return {
        ...state,
        dashboardLoad: {
          ...state.dashboardLoad,
          bootstrapStatus: 'loading',
          error: null,
        },
      }

    case 'BOOTSTRAP_SUCCEEDED': {
      const filters = createDashboardGlobalFilterState(action.dashboard)
      return {
        dashboard: action.dashboard,
        dashboardId: action.dashboardId,
        dashboardLoad: {
          ...state.dashboardLoad,
          bootstrapStatus: 'ready',
          error: null,
          initialLoadCompleted: false,
        },
        draftFilters: filters,
        appliedFilters: filters,
        slicerOptions: {
          byField: {},
          loadingByField: {},
          errorByField: {},
          requestKeyByField: {},
        },
        widgets: createWidgetStateMap(action.dashboard),
      }
    }

    case 'DASHBOARD_REPLACED_SELECTIVE': {
      const filters = createDashboardGlobalFilterState(action.dashboard)
      const preserveWidgetIds = new Set(action.preserveWidgetIds)
      return {
        dashboard: action.dashboard,
        dashboardId: action.dashboardId,
        dashboardLoad: {
          ...state.dashboardLoad,
          bootstrapStatus: 'ready',
          error: null,
          initialLoadCompleted: true,
        },
        draftFilters: filters,
        appliedFilters: filters,
        slicerOptions: {
          byField: {},
          loadingByField: {},
          errorByField: {},
          requestKeyByField: {},
        },
        widgets: Object.fromEntries(
          action.dashboard.widgets.map((widget) => {
            const preservedSession = preserveWidgetIds.has(widget.id) ? state.widgets[widget.id] : null
            return [
              widget.id,
              preservedSession ?? createWidgetSessionState(widget),
            ]
          }),
        ),
      }
    }

    case 'BOOTSTRAP_FAILED':
      return {
        ...state,
        dashboardLoad: {
          ...state.dashboardLoad,
          bootstrapStatus: 'error',
          error: action.error,
        },
      }

    case 'SET_DASHBOARD_ID':
      return {
        ...state,
        dashboardId: action.dashboardId,
      }

    case 'DRAFT_SLICER_CHANGED':
      return {
        ...state,
        draftFilters: {
          ...state.draftFilters,
          slicerValues: {
            ...state.draftFilters.slicerValues,
            [action.field]: action.value,
          },
        },
      }

    case 'DRAFT_DATE_RANGE_CHANGED':
      return {
        ...state,
        draftFilters: {
          ...state.draftFilters,
          dateRange: { ...action.dateRange },
        },
      }

    case 'DATE_DEFAULT_RESOLVED': {
      // Before the first load, the date range was resolved without the catalog: an older
      // dashboard (no stored date mode) sits on the custom range and a stored preset is
      // provisional. Apply the catalog's resolution (its default, or custom for a preset it
      // does not list) to both the draft and applied filters so the first requests use it.
      // A range the user has since changed is left alone.
      const provisional = createDashboardGlobalFilterState(state.dashboard).dateRange
      const resolved = createDashboardGlobalFilterState(state.dashboard, action.datePresetOptions).dateRange
      if (areDateRangesEqual(provisional, resolved)) return state
      if (!areDateRangesEqual(state.draftFilters.dateRange, provisional)) return state
      if (!areDateRangesEqual(state.appliedFilters.dateRange, provisional)) return state

      return {
        ...state,
        draftFilters: { ...state.draftFilters, dateRange: { ...resolved } },
        appliedFilters: { ...state.appliedFilters, dateRange: { ...resolved } },
      }
    }

    case 'DRAFT_FILTERS_RESET':
      return {
        ...state,
        draftFilters: {
          slicerValues: {},
          dateRange: createEmptyDashboardDateRange(),
        },
      }

    case 'APPLIED_FILTERS_COMMITTED':
      return {
        ...state,
        appliedFilters: {
          slicerValues: { ...state.draftFilters.slicerValues },
          dateRange: { ...state.draftFilters.dateRange },
        },
        widgets: Object.fromEntries(
          Object.entries(state.widgets).map(([widgetId, session]) => [
            widgetId,
            {
              ...session,
              query: {
                ...session.query,
                page: 1,
                drilldown: null,
              },
              ui: {
                ...session.ui,
                pickerState: null,
              },
            },
          ]),
        ),
      }

    case 'VIEW_APPLIED':
      // Loaded data stays on screen; the next requests mark each widget as refreshing.
      return {
        ...state,
        draftFilters: {
          slicerValues: { ...action.filters.slicerValues },
          dateRange: { ...action.filters.dateRange },
        },
        appliedFilters: {
          slicerValues: { ...action.filters.slicerValues },
          dateRange: { ...action.filters.dateRange },
        },
        widgets: Object.fromEntries(
          Object.entries(state.widgets).map(([widgetId, session]) => [
            widgetId,
            {
              ...session,
              query: action.widgetQueries[widgetId]
                ? { ...action.widgetQueries[widgetId], page: 1 }
                : { ...session.query, page: 1 },
              ui: {
                pendingSort: null,
                pickerState: null,
              },
            },
          ]),
        ),
      }

    case 'SLICER_OPTIONS_LOAD_STARTED': {
      const fields = Object.keys(action.requestKeyByField)
      return {
        ...state,
        slicerOptions: {
          ...state.slicerOptions,
          loadingByField: {
            ...state.slicerOptions.loadingByField,
            ...Object.fromEntries(fields.map((field) => [field, true])),
          },
          errorByField: {
            ...state.slicerOptions.errorByField,
            ...Object.fromEntries(fields.map((field) => [field, null])),
          },
          requestKeyByField: {
            ...state.slicerOptions.requestKeyByField,
            ...action.requestKeyByField,
          },
        },
      }
    }

    case 'SLICER_OPTIONS_LOAD_SUCCEEDED': {
      if (state.slicerOptions.requestKeyByField[action.field] !== action.requestKey) {
        return state
      }

      return {
        ...state,
        slicerOptions: {
          ...state.slicerOptions,
          byField: {
            ...state.slicerOptions.byField,
            [action.field]: action.options,
          },
          loadingByField: {
            ...state.slicerOptions.loadingByField,
            [action.field]: false,
          },
          errorByField: {
            ...state.slicerOptions.errorByField,
            [action.field]: null,
          },
        },
      }
    }

    case 'SLICER_OPTIONS_LOAD_FAILED': {
      if (state.slicerOptions.requestKeyByField[action.field] !== action.requestKey) {
        return state
      }

      return {
        ...state,
        slicerOptions: {
          ...state.slicerOptions,
          byField: {
            ...state.slicerOptions.byField,
            [action.field]: [],
          },
          loadingByField: {
            ...state.slicerOptions.loadingByField,
            [action.field]: false,
          },
          errorByField: {
            ...state.slicerOptions.errorByField,
            [action.field]: action.error,
          },
        },
      }
    }

    case 'WIDGET_SORT_SET': {
      const session = getWidgetSession(state, action.widgetId)
      return {
        ...state,
        widgets: {
          ...state.widgets,
          [action.widgetId]: {
            ...session,
            query: {
              ...session.query,
              page: 1,
              sortColumn: action.column,
              sortDirection: action.direction,
            },
          },
        },
      }
    }

    case 'WIDGET_PAGE_SET': {
      const session = getWidgetSession(state, action.widgetId)
      return {
        ...state,
        widgets: {
          ...state.widgets,
          [action.widgetId]: {
            ...session,
            query: {
              ...session.query,
              page: action.page,
            },
          },
        },
      }
    }

    case 'WIDGET_DIMENSION_SET': {
      const session = getWidgetSession(state, action.widgetId)
      const widget = state.dashboard?.widgets.find((item) => item.id === action.widgetId)
      // A sort on the old row dimension (or none) moves to the new one's default: oldest first for dates,
      // otherwise none. A measure sort the user chose is kept.
      const resetSort = widget && widget.type !== 'line'
        && (!session.query.sortColumn || isDimensionSortKey(widget, session.query.sortColumn))
      const dateSortColumn = resetSort ? resolveDefaultDateSortKey(widget, action.groupByDimension) : null
      return {
        ...state,
        widgets: {
          ...state.widgets,
          [action.widgetId]: {
            ...session,
            query: {
              ...session.query,
              page: 1,
              ...(resetSort ? { sortColumn: dateSortColumn, sortDirection: dateSortColumn ? 'asc' as const : 'desc' as const } : {}),
              groupByDimension: action.groupByDimension,
              groupByDimensions: action.groupByDimensions,
              drilldown: null,
            },
            ui: {
              ...session.ui,
              pickerState: null,
            },
          },
        },
      }
    }

    case 'WIDGET_MEASURE_PARAMETER_SET': {
      const session = getWidgetSession(state, action.widgetId)
      return {
        ...state,
        widgets: {
          ...state.widgets,
          [action.widgetId]: {
            ...session,
            query: {
              ...session.query,
              page: 1,
              measureParameterSelections: action.selections,
            },
          },
        },
      }
    }

    case 'WIDGET_DRILLDOWN_SET': {
      const session = getWidgetSession(state, action.widgetId)
      return {
        ...state,
        widgets: {
          ...state.widgets,
          [action.widgetId]: {
            ...session,
            query: {
              ...session.query,
              page: 1,
              drilldown: action.drilldown,
            },
            ui: {
              ...session.ui,
              pickerState: null,
            },
          },
        },
      }
    }

    case 'WIDGET_DRILLDOWN_CLEARED': {
      const session = getWidgetSession(state, action.widgetId)
      return {
        ...state,
        widgets: {
          ...state.widgets,
          [action.widgetId]: {
            ...session,
            query: {
              ...session.query,
              page: 1,
              drilldown: null,
            },
            ui: {
              ...session.ui,
              pickerState: null,
            },
          },
        },
      }
    }

    case 'WIDGET_PENDING_SORT_SET': {
      const session = getWidgetSession(state, action.widgetId)
      return {
        ...state,
        widgets: {
          ...state.widgets,
          [action.widgetId]: {
            ...session,
            ui: {
              ...session.ui,
              pendingSort: action.pendingSort,
            },
          },
        },
      }
    }

    case 'WIDGET_PICKER_SET': {
      const session = getWidgetSession(state, action.widgetId)
      return {
        ...state,
        widgets: {
          ...state.widgets,
          [action.widgetId]: {
            ...session,
            ui: {
              ...session.ui,
              pickerState: action.pickerState,
            },
          },
        },
      }
    }

    case 'WIDGET_REQUEST_STARTED': {
      const session = getWidgetSession(state, action.widgetId)
      return {
        ...state,
        widgets: {
          ...state.widgets,
          [action.widgetId]: {
            ...session,
            status: hasWidgetData(session.data) ? 'refreshing' : 'loading',
            error: null,
            request: {
              ...session.request,
              requestId: action.requestId,
              inFlight: true,
              currentIntentKey: action.intentKey,
              queuedIntent: null,
            },
          },
        },
      }
    }

    case 'WIDGET_REQUEST_QUEUED': {
      const session = getWidgetSession(state, action.widgetId)
      return {
        ...state,
        widgets: {
          ...state.widgets,
          [action.widgetId]: {
            ...session,
            request: {
              ...session.request,
              queuedIntent: action.intent,
            },
          },
        },
      }
    }

    case 'WIDGET_REQUEST_SUCCEEDED': {
      const session = getWidgetSession(state, action.widgetId)
      if (session.request.requestId !== action.requestId) {
        return state
      }

      return {
        ...state,
        widgets: {
          ...state.widgets,
          [action.widgetId]: {
            ...session,
            status: 'live',
            error: null,
            data: action.data,
            request: {
              ...session.request,
              lastCompletedIntentKey: action.intentKey,
            },
            ui: {
              ...session.ui,
              pendingSort: null,
            },
          },
        },
      }
    }

    case 'WIDGET_REQUEST_FAILED': {
      const session = getWidgetSession(state, action.widgetId)
      if (session.request.requestId !== action.requestId) {
        return state
      }

      return {
        ...state,
        widgets: {
          ...state.widgets,
          [action.widgetId]: {
            ...session,
            status: 'error',
            error: action.error,
            request: {
              ...session.request,
              lastCompletedIntentKey: action.intentKey,
            },
            ui: {
              ...session.ui,
              pendingSort: null,
            },
          },
        },
      }
    }

    case 'WIDGET_REQUEST_FINISHED': {
      const session = getWidgetSession(state, action.widgetId)
      if (session.request.requestId !== action.requestId) {
        return state
      }

      return {
        ...state,
        widgets: {
          ...state.widgets,
          [action.widgetId]: {
            ...session,
            request: {
              ...session.request,
              inFlight: false,
              currentIntentKey: null,
            },
          },
        },
      }
    }

    case 'REFRESH_ALL_STARTED':
      return {
        ...state,
        dashboardLoad: {
          ...state.dashboardLoad,
          error: null,
        },
      }

    case 'REFRESH_ALL_FINISHED':
      return {
        ...state,
        dashboardLoad: {
          ...state.dashboardLoad,
          initialLoadCompleted: true,
          refreshNonce: state.dashboardLoad.refreshNonce + 1,
        },
      }

    default:
      return state
  }
}

export function createHydratableWidgetStateMap(state: DashboardViewState) {
  if (!state.dashboard) return {}
  return Object.fromEntries(
    Object.entries(state.widgets).filter(([widgetId]) => {
      const widget = state.dashboard?.widgets.find((item) => item.id === widgetId)
      return widget ? hasWidgetHydrationPayload(widget) : false
    }),
  )
}
